import {
  Button,
  Flex,
  Spinner,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import FileSaver from 'file-saver';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  date as commonDate,
  finleyAssert,
  typeguards as commonTypeguards,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Accounting, Reporting } from '@finley-technologies/finley-types';

import { useCreateInvoiceContext } from '../../hooks/useCreateInvoice';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { useGetCurrentCreditAgreementFullArtifactQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import {
  useGetReportS3DownloadUrlByReportingWorkleaseRecordIdLazyQueryGQLType,
  useProduceReportingGenerateMessageMutation,
} from 'packages/reporting/requests/reporting.gql.generated';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

type DownloadInvoicePreviewButtonBodyParams = {
  creditFacilityId: string;
  creditAgreementId: string;
  reportName: string;
};
const DownloadInvoicePreviewButtonBody = ({
  creditFacilityId,
  creditAgreementId,
  reportName,
}: DownloadInvoicePreviewButtonBodyParams) => {
  const toast = useToast();
  const { user } = useUserAuthOrThrow();
  const {
    invoice: previewInvoice,
    invoiceComponentsWithInvoiceItems: previewInvoiceComponentsWithInvoiceItems,
  } = useCreateInvoiceContext();

  const [
    currentPollingWorkleaseRecordId,
    setCurrentPollingWorkleaseRecordId,
  ] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [produceGenerateMessage] = useProduceReportingGenerateMessageMutation({
    fetchPolicy: 'no-cache',
  });

  const [
    getReportS3DownloadUrlByWorklease,
    { data: reportS3DownloadUrlByWorkleaseData },
  ] = useGetReportS3DownloadUrlByReportingWorkleaseRecordIdLazyQueryGQLType({
    pollInterval: currentPollingWorkleaseRecordId ? 1000 : 0, // poll every second if 'currentPollingReport' exists
    fetchPolicy: 'no-cache',
  });

  const generatePreviewInvoice = useCallback(async () => {
    setIsLoading(true);

    const previewInvoiceForWorklease = {
      ...previewInvoice,

      id: 'Preview',
      finleyLoanId: previewInvoice.finleyLoanId ?? null,
      originatingInvoiceId: previewInvoice.originatingInvoiceId ?? null,
      sourceBankAccountId: previewInvoice.sourceBankAccountId ?? null,
      destinationBankAccountId: previewInvoice.destinationBankAccountId ?? null,

      memo: previewInvoice.memo ?? null,
      targetInvoiceDate: previewInvoice.targetInvoiceDate ?? null,
      paymentDate: previewInvoice.paymentDate ?? null,

      created_at: new Date(),
      updated_at: new Date(),
    };

    const previewInvoiceComponentsWithInvoiceItemsForWorklease = previewInvoiceComponentsWithInvoiceItems.map(
      (invoiceComponentWithInvoiceItems) => {
        const previewInvoiceItemsForInvoiceComponent = invoiceComponentWithInvoiceItems.invoiceItems.map(
          (invoiceItem) => ({
            ...invoiceItem,

            accountingAccount: {
              ...invoiceItem.accountingAccount,
              tAccount: commonTypeguards.toEnum<Accounting.TAccount>(
                invoiceItem.accountingAccount.tAccount,
              ),
            },

            id: 'Preview',
            invoiceId: 'Preview',
            invoiceComponentId: 'Preview',
            accountingTransactionId: null,
            capitalizedLoanId: null,

            created_at: new Date(),
            updated_at: new Date(),
          }),
        );

        return {
          invoiceComponent: {
            id: 'Preview',
            invoiceId: 'Preview',
            finleyLoanId: previewInvoice.finleyLoanId ?? null,
            invoiceComponentType: previewInvoice.invoiceType,
            totalAmount: previewInvoice.totalAmount,
            accrualStartDate: previewInvoice.accrualStartDate,
            accrualEndDate: previewInvoice.accrualEndDate,
            created_at: new Date(),
            updated_at: new Date(),
          },
          invoiceItems: previewInvoiceItemsForInvoiceComponent,
        };
      },
    );

    const worklease: Reporting.InvoiceReportingGenerateQaWorklease = {
      initiatingOrganizationId: user.organizationId,
      creditFacilityId,
      creditAgreementId,
      reportName,
      reportingDate: commonDate.currentDateStr(),
      mode: 'QA',
      reportTemplateFileType: 'docx',
      reportConfigType: 'invoice',
      config: {
        invoiceId: null,
        previewInvoice: previewInvoiceForWorklease,
        previewInvoiceComponentsWithInvoiceItems: previewInvoiceComponentsWithInvoiceItemsForWorklease,
      },
    };

    const { data } = await produceGenerateMessage({
      variables: {
        worklease,
        parameters: null,
      },
    });
    finleyAssert(
      data?.result,
      'Expecting a result from "produceGenerateMessage".',
    );

    setCurrentPollingWorkleaseRecordId(data.result.recordId);

    getReportS3DownloadUrlByWorklease({
      variables: {
        reportConfigType: 'invoice',
        reportingWorkleaseRecordId: data.result.recordId,
      },
    });

    toast({
      ...successToastConfig,
      title: 'Generating invoice preview',
    });
  }, [
    creditFacilityId,
    produceGenerateMessage,
    user.organizationId,
    previewInvoice,
    previewInvoiceComponentsWithInvoiceItems,
    reportName,
    creditAgreementId,
    getReportS3DownloadUrlByWorklease,
    toast,
  ]);

  useEffect(() => {
    const handleWorkleaseStatus = async () => {
      // if worklease is initialized state, return and continue polling
      if (
        !reportS3DownloadUrlByWorkleaseData?.getReportS3DownloadUrlByReportingWorkleaseRecordId ||
        reportS3DownloadUrlByWorkleaseData
          .getReportS3DownloadUrlByReportingWorkleaseRecordId
          .workleaseStatus === 'initialized'
      ) {
        return;
      }

      const {
        getReportS3DownloadUrlByReportingWorkleaseRecordId: {
          workleaseStatus,
          s3DownloadUrl,
          error,
        },
      } = reportS3DownloadUrlByWorkleaseData;

      // if worklease is in error state, toast error and stop polling
      if (workleaseStatus === 'error' || !s3DownloadUrl) {
        const errorToastDescription =
          commonUtils.EnvUtils.isProduction() || !error ? undefined : error;

        toast({
          ...errorToastConfig,
          title: `Failed to retrieve valid download URL [report:${reportName}]`,
          description: errorToastDescription,
        });

        setIsLoading(false);
        setCurrentPollingWorkleaseRecordId(undefined);
        return;
      }

      // if worklease is in success state, download file from S3 url and stop polling
      if (workleaseStatus === 'success') {
        setCurrentPollingWorkleaseRecordId(undefined);

        try {
          const response = await axios({
            url: s3DownloadUrl,
            method: 'GET',
            responseType: 'blob',
          });

          const fileBlob = new Blob([response.data], {
            type: response.data.type,
          });
          const fileName = `${reportName}-preview-${commonDate.currentDateStr()}`;
          FileSaver.saveAs(fileBlob, fileName);
        } catch (err) {
          errorToastConfig.title = commonUtils.EnvUtils.isProduction()
            ? 'Failed to retrieve report preview. Please contact Finley for support.'
            : JSON.stringify(err);

          toast(errorToastConfig);
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (currentPollingWorkleaseRecordId) {
      // if there is a worklease we are currently polling for,
      // handle the worklease results
      handleWorkleaseStatus();
    }
  }, [
    reportName,
    reportS3DownloadUrlByWorkleaseData,
    toast,
    currentPollingWorkleaseRecordId,
  ]);

  return (
    <Tooltip
      label="Generate and download invoice preview"
      placement="bottom-end"
    >
      <Button
        variant="unstyled"
        onClick={async () => {
          await generatePreviewInvoice();
        }}
        isDisabled={isLoading}
        color={colors.gray[500]}
      >
        <Flex alignItems="center">
          {isLoading ? <Spinner /> : <Icon icon="arrow-down-to-bracket" />}
          <Text fontSize="14px" ml="8px">
            {isLoading ? 'Generating invoice preview' : 'Download Preview'}
          </Text>
        </Flex>
      </Button>
    </Tooltip>
  );
};

type DownloadInvoicePreviewButtonParams = {
  creditFacilityId: string;
};
export const DownloadInvoicePreviewButton = ({
  creditFacilityId,
}: DownloadInvoicePreviewButtonParams) => {
  const toast = useToast();
  const {
    data: creditAgreementData,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    onError: () => {
      errorToastConfig.title =
        'An unexpected error occurred when fetching the current credit agreement. Please contact Finley for support.';

      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
    },
    fetchPolicy: 'no-cache',
  });

  const creditAgreement = useMemo(
    () =>
      parseCreditAgreementFullArtifact(
        creditAgreementData?.currentCreditAgreementFullArtifact
          ?.creditAgreement,
      ),
    [creditAgreementData],
  );

  if (!creditAgreement) {
    return <Button variant="v2-ghost" isLoading />;
  }

  const reportingProperty = creditAgreement.reportingMechanics.find(
    (reportingMechanic) =>
      reportingMechanic.reportConfigTypes.includes('invoice'),
  );
  const reportName = reportingProperty?.name;

  return (
    <>
      {reportName ? (
        <DownloadInvoicePreviewButtonBody
          creditFacilityId={creditFacilityId}
          creditAgreementId={creditAgreement.id}
          reportName={reportName}
        />
      ) : (
        <Tooltip
          label="Invoice template is not configured in the Credit Agreement"
          placement="bottom-end"
        >
          <Button isDisabled variant="unstyled" color={colors.gray[500]}>
            <Flex alignItems="center">
              <Icon icon="arrow-down-to-bracket" />
              <Text ml="8px" fontSize="14px">
                Download Preview
              </Text>
            </Flex>
          </Button>
        </Tooltip>
      )}
    </>
  );
};
